import type { ReactNode } from 'react';
import { Box, Collapse, Flex, Text, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  label: string;
  summary: string;
  colorHex: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
};

export default function StyleRow({
  label,
  summary,
  colorHex,
  expanded,
  onToggle,
  children,
}: Props) {
  const theme = useMantineTheme();

  return (
    <Box
      style={{
        borderRadius: 16,
        border: `1px solid ${expanded ? theme.colors.gray[3] : theme.colors.gray[2]}`,
        transition: 'border-color 120ms ease',
        overflow: 'hidden',
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        px="md"
        py="sm"
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          backgroundColor: expanded ? 'white' : undefined,
          transition: 'background-color 120ms ease',
          ':hover': { backgroundColor: theme.colors.gray[0] },
        }}
        className="style-row-header"
      >
        <Flex align="center" gap="md" flex={1}>
          <Text size="textMd" fw={FONT_WEIGHTS.semibold} style={{ minWidth: 100 }}>
            {label}
          </Text>
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
            {summary}
          </Text>
        </Flex>

        <Flex align="center" gap="sm">
          <Box
            w={16}
            h={16}
            style={{
              borderRadius: '50%',
              backgroundColor: `#${colorHex}`,
              border: `1px solid ${theme.colors.gray[3]}`,
              flexShrink: 0,
            }}
          />
          {expanded ? (
            <IconChevronDown size={18} color={theme.colors.gray[5]} />
          ) : (
            <IconChevronRight size={18} color={theme.colors.gray[5]} />
          )}
        </Flex>
      </Flex>

      <Collapse in={expanded}>
        <Box
          px="md"
          pb="md"
          pt="xs"
          style={{
            borderTop: `1px solid ${theme.colors.gray[2]}`,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}
